"use client";

import { c as _c } from "react/compiler-runtime";
import { PartialRecord } from "@/types/common";
import { LocaleMessageType } from "@/types/locale";
import { DocumentActionsEntityState, useDocumentActionsContext } from "@hooks/pages/use-document-actions";
import { useRouteInfo } from "@hooks/pages/use-route-info";
import React, { useMemo } from "react";
import { LocaleString } from "@components/common/content/locale/locale-string";
import { DocumentActionsClient } from "@components/document/document-actions-client";
import { useAnyTranslations } from "@lib/i18n/utils";

/**
 * Tries to derive a human-readable, localized title for the current "document" (domain entity).
 *
 * Business rules:
 * - If an explicit `entityTitleField` path is provided in the DocumentActionsEntityState, we navigate the entity object
 *   using a dotted path (e.g. "content.data.title" or "speaker.name.en") and return the value found there.
 *   This allows pages to override the default title source when an entity has multiple potential title fields.
 * - If `entityTitleField` is not provided, we fall back to CMS content title (Squidex-like shape): `entity.content.data.title`.
 * - Result can be either:
 *   - LocaleMessageType (localizable object), which downstream components (e.g. <LocaleString/>) can render per active locale
 *   - string, if the title is already resolved
 *   - undefined, when neither an override nor a fallback is available
 *
 * Notes on typing:
 * - The dotted path traversal uses `reduce` with index access on `any`-like data, hence the `@ts-expect-error`.
 *   This is intentional to support flexible field paths coming from configuration.
 */
export const extractEntityTitleForFromDocumentContext = (state?: DocumentActionsEntityState): LocaleMessageType | string | undefined => {
  const {
    entity,
    entityTitleField
  } = state ?? {};
  const entityContentData = entity?.content?.data as PartialRecord<string, LocaleMessageType> | undefined;
  return entityTitleField ?
  // @ts-expect-error any key
  entityTitleField?.split(".").reduce((acc, key) => acc?.[key], entity as never) as LocaleMessageType | string | undefined : entityContentData?.title;
};
export const DocumentTitle = () => {
  const $ = _c(22);
  const tAny = useAnyTranslations();
  const {
    localizationKeys
  } = useRouteInfo()?.page ?? {};
  const documentActions = useDocumentActionsContext();
  const t0 = documentActions.state?.entity;
  let t1;
  if ($[0] !== t0) {
    t1 = extractEntityTitleForFromDocumentContext(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const entityTitle = t1;
  if (!localizationKeys) {
    return null;
  }
  let t2;
  if ($[2] !== entityTitle || $[3] !== localizationKeys.title || $[4] !== tAny) {
    t2 = !entityTitle && <h1 className="text-xl font-bold tracking-tight text-pretty">{tAny(localizationKeys.title)}</h1>;
    $[2] = entityTitle;
    $[3] = localizationKeys.title;
    $[4] = tAny;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== entityTitle || $[7] !== localizationKeys.title || $[8] !== tAny) {
    t3 = entityTitle && <h1 className="text-xl font-bold tracking-tight text-pretty">{tAny(localizationKeys.title)} - <LocaleString content={entityTitle} /></h1>;
    $[6] = entityTitle;
    $[7] = localizationKeys.title;
    $[8] = tAny;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== localizationKeys.description || $[11] !== tAny) {
    t4 = tAny(localizationKeys.description);
    $[10] = localizationKeys.description;
    $[11] = tAny;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t4) {
    t5 = <p className="text-muted-foreground">{t4}</p>;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t2 || $[16] !== t3 || $[17] !== t5) {
    t6 = <div className="2xl:max-w-4/10">{t2}{t3}{t5}</div>;
    $[15] = t2;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <DocumentActionsClient className="ml-auto" />;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6) {
    t8 = <div className="flex flex-wrap items-center gap-2 space-y-0.5 bg-sidebar p-2 md:p-4">{t6}{t7}</div>;
    $[20] = t6;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};